import { defineStore } from "pinia";
import { useContentsStore } from "./ContentsStore";
import { ArticleInfo, type ArticleFileItem } from "~/entities/ArticleInfo";
import { ContentList } from "~/entities/ContentList";

export const useArticleStore = defineStore("ArticleStore", () => {
  const contentStore = useContentsStore();

  const articles = ref<ContentList<ArticleInfo>>();
  const article = ref<ArticleInfo>();
  const loading = ref(false);

  // articleBody内のfileKeyををfileUrlに変換する
  const convertFileKeyToImgSrc = (articleBody: string, fileKeys: ArticleFileItem[]) => {
    let newArticleBody = articleBody;
    fileKeys.forEach(file => {
      newArticleBody = newArticleBody.replace(`{{ fileKey:${file.fileKey} }}`, file.fileUrl);
    });
    return newArticleBody;
  };

  // articleBody内のimgタグのsrcをfileKeyに変換する
  const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const convertImgSrcToFileKey = (articleBody: string, fileKeys: ArticleFileItem[]) => {
    let newArticleBody = articleBody;
    fileKeys.forEach(file => {
      // クエリパラメータを除外
      const urlWithoutQuery = file.fileUrl.split("?")[0];
      const regex = new RegExp(escapeRegExp(urlWithoutQuery) + "(\\?[^\"'\\s]*)?", "g");
      newArticleBody = newArticleBody.replace(regex, `{{ fileKey:${file.fileKey} }}`);
    });
    return newArticleBody;
  };

  const createArticleBody = (blocks: { id: number; text: string }[]) => {
    let articleBody = "";
    if (blocks.length > 0) {
      articleBody += blocks.map(block => "<PlanEdit/>" + block.text).join("");
    }
    return articleBody;
  };

  // <PlanEdit/>で分割されたブロックを取得
  // isEdit = falseの場合は有料プランの紹介文を表示する
  const divideBlocks = (articleBody: string, isEdit: boolean, isContract: boolean) => {
    let addMemberOnly = false;
    if (!isEdit) {
      if (!isContract && articleBody.includes("<memberonly>")) {
        // メンバーではない場合 <memberonly>以下を削除
        articleBody = articleBody.split("<memberonly>")[0];
        addMemberOnly = true;
      }
      if (isContract && articleBody.includes("<memberonly>")) {
        // 一つ目の<memberonly></memberonly>を<div class="hr-with-text"><span>この先は会員限定です</span></div>に置換
        articleBody = articleBody.replace(
          "<memberonly></memberonly>",
          '<div class="hr-with-text"><span>この先は会員限定です</span></div>',
        );
      }
    }
    const contents = articleBody.split("<PlanEdit/>");
    const blocks = contents
      .map((content, index) => {
        if (content === "") return null;
        // ui, liなどは改行があると表示されないのでreplace
        const description = content.replace(/<ul>\n/g, "<ul>").replace(/<\/li>\n/g, "</li>");
        return { id: index, text: description };
      })
      .filter(block => block !== null) as { id: number; text: string }[];
    if (addMemberOnly) {
      // メンバーではなく有料会員エリアがある場合
      // <memberonly/>のみのデータを追加
      blocks.push({ id: blocks.length, text: "<memberonly/>" });
    }

    return blocks;
  };

  const setArticle = (articleInfo: ArticleInfo) => {
    article.value = JSON.parse(JSON.stringify(articleInfo));
  };

  const fetchOne = async (contentsId: string) => {
    loading.value = true;
    article.value = undefined;
    try {
      const res = await contentStore.getDetail<ArticleInfo>(contentsId);
      if (res) {
        if (!res.articleFiles) {
          res.articleFiles = [];
        }
        // articleBody内のfileKeyををfileUrlに変換する
        res.articleBody = convertFileKeyToImgSrc(res.articleBody, res.articleFiles || []);
        article.value = res;
      }
    } finally {
      loading.value = false;
    }
  };

  // optionsにis_free, search_wordが入ります。
  const fetch = async (page: number, pageSize: number, planId: string | undefined, options?: Record<string, any>) => {
    loading.value = true;
    articles.value = undefined;
    try {
      articles.value = await contentStore.getList<ContentList<ArticleInfo>>(page, pageSize, {
        contents_types: ["article"],
        plan_id: planId,
        ...options,
      });
    } finally {
      loading.value = false;
    }
  };

  return {
    article,
    articles,
    loading,
    convertFileKeyToImgSrc,
    convertImgSrcToFileKey,
    createArticleBody,
    divideBlocks,
    setArticle,
    fetchOne,
    fetch,
  };
});
